/**
 * CheckinPage - Quick Check-in
 * Il Diario di Bordo - SaaS
 *
 * Griglia rapida per segnare le presenze giornaliere dei minori.
 * Salva nella collection Checkin (non crea Entry).
 *
 * - Desktop: iniziali + nome + stato colore
 * - Mobile: solo nome + stato colore (no avatar)
 * - Educatori: prima i propri minori, poi gli altri espandibili
 * - Data di nascita solo se omonimi nel centro
 */

import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { minoriApi, entriesApi } from '../services/api'
import { Zap, Check, Loader2, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const extractMinori = (response) => {
  const data = response?.data
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.minori)) return data.minori
  if (Array.isArray(data?.data?.minori)) return data.data.minori
  return []
}

/**
 * Estrae le iniziali da nomeCompleto (es. "Marco Rossi" → "MR")
 */
const getInitials = (nomeCompleto) => {
  if (!nomeCompleto) return '?'
  const parts = nomeCompleto.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return nomeCompleto[0].toUpperCase()
}

/**
 * Formatta la data di nascita in formato breve (es. "15/03/2014")
 */
const formatBirthDate = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('it-IT')
}

/**
 * Trova i nomi duplicati nel set di minori per decidere se mostrare la data di nascita
 */
const findDuplicateNames = (minori) => {
  const nameCount = {}
  minori.forEach(m => {
    const name = (m.nomeCompleto || '').toLowerCase().trim()
    nameCount[name] = (nameCount[name] || 0) + 1
  })
  return new Set(
    Object.entries(nameCount)
      .filter(([, count]) => count > 1)
      .map(([name]) => name)
  )
}

export default function CheckinPage() {
  const { user } = useAuth()
  const { showSuccess, showError } = useNotification()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [minori, setMinori] = useState([])
  const [presenze, setPresenze] = useState({}) // { minoreId: { presente: true/false, orarioIngresso: '14:02' } }
  const [showAltri, setShowAltri] = useState(false)

  const isEducatore = user?.role === 'educatore'

  useEffect(() => {
    loadMinori()
  }, [])

  const loadMinori = async () => {
    try {
      setLoading(true)
      const [minoriRes, checkinsRes] = await Promise.all([
        minoriApi.list({ attivo: true, limit: 500 }),
        entriesApi.getCheckinsToday()
      ])
      const list = extractMinori(minoriRes)
      setMinori(list)

      // Mappa checkin esistenti per minoreId
      const todayCheckins = checkinsRes.data?.data || checkinsRes.data || []
      const checkinMap = {}
      todayCheckins.forEach(c => {
        checkinMap[c.minoreId] = c
      })

      // Inizializza presenze: ripristina da checkin esistenti o segna come "da segnare"
      const initial = {}
      list.forEach(m => {
        const existing = checkinMap[m._id]
        if (existing) {
          const isPresente = existing.presente === true || existing.presente === 'Presente' || existing.presente === 'Sì'
          initial[m._id] = {
            presente: isPresente,
            orarioIngresso: existing.orarioIngresso || null
          }
        } else {
          initial[m._id] = { presente: null }
        }
      })
      setPresenze(initial)
    } catch (error) {
      showError('Errore nel caricamento dei minori')
    } finally {
      setLoading(false)
    }
  }

  // Separa minori assegnati vs altri (per educatori)
  const { mieiMinori, altriMinori } = useMemo(() => {
    if (!isEducatore || !user?._id) {
      return { mieiMinori: minori, altriMinori: [] }
    }

    const miei = []
    const altri = []
    minori.forEach(m => {
      const refId = m.educatoreReferente?._id || m.educatoreReferente
      if (refId && refId.toString() === user._id.toString()) {
        miei.push(m)
      } else {
        altri.push(m)
      }
    })

    // Ordine alfabetico per entrambe le sezioni
    const sortByName = (a, b) => (a.nomeCompleto || '').localeCompare(b.nomeCompleto || '')
    miei.sort(sortByName)
    altri.sort(sortByName)

    return { mieiMinori: miei, altriMinori: altri }
  }, [minori, user, isEducatore])

  // Nomi duplicati per mostrare data di nascita
  const duplicateNames = useMemo(() => findDuplicateNames(minori), [minori])

  const togglePresenza = (minoreId) => {
    setPresenze(prev => {
      const current = prev[minoreId]
      if (current.presente === null || current.presente === false) {
        // Non segnato o assente -> presente
        const ora = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        return { ...prev, [minoreId]: { presente: true, orarioIngresso: ora } }
      } else {
        // Presente -> assente
        return { ...prev, [minoreId]: { presente: false, orarioIngresso: null } }
      }
    })
  }

  const setAssente = (minoreId) => {
    setPresenze(prev => ({
      ...prev,
      [minoreId]: { presente: false, orarioIngresso: null }
    }))
  }

  const handleConfirm = async () => {
    const daInviare = Object.entries(presenze)
      .filter(([, v]) => v.presente !== null)
      .map(([minoreId, v]) => ({
        minoreId,
        presente: v.presente,
        orarioIngresso: v.orarioIngresso || undefined
      }))

    if (daInviare.length === 0) {
      showError('Segna almeno una presenza prima di confermare')
      return
    }

    setSaving(true)
    try {
      const res = await entriesApi.bulkCheckin({ presenze: daInviare })
      const data = res.data?.data || res.data
      showSuccess(`Check-in completato: ${data.created || 0} creati, ${data.updated || 0} aggiornati`)
      navigate('/dashboard')
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio del check-in')
    } finally {
      setSaving(false)
    }
  }

  // Conteggi
  const presenti = Object.values(presenze).filter(v => v.presente === true).length
  const assenti = Object.values(presenze).filter(v => v.presente === false).length
  const daSegnare = Object.values(presenze).filter(v => v.presente === null).length

  // Rendering del singolo card minore
  const renderMinoreCard = (minore) => {
    const stato = presenze[minore._id]
    const isPresente = stato?.presente === true
    const isAssente = stato?.presente === false
    const isDuplicate = duplicateNames.has((minore.nomeCompleto || '').toLowerCase().trim())

    return (
      <div
        key={minore._id}
        onClick={() => togglePresenza(minore._id)}
        onContextMenu={(e) => {
          e.preventDefault()
          setAssente(minore._id)
        }}
        className={`
          relative p-3 sm:p-4 rounded-xl border-2 cursor-pointer select-none
          transition-all duration-200 active:scale-95
          ${isPresente
            ? 'border-green-400 bg-green-50 dark:bg-green-900/20 dark:border-green-600 shadow-sm shadow-green-200 dark:shadow-green-900/30'
            : isAssente
              ? 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-600 shadow-sm shadow-red-200 dark:shadow-red-900/30'
              : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
          }
        `}
      >
        {/* Badge stato */}
        {isPresente && (
          <div className="absolute top-2 right-2">
            <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
        )}
        {isAssente && (
          <div className="absolute top-2 right-2 text-xs font-bold text-red-600 dark:text-red-400">
            A
          </div>
        )}

        {/* Desktop: Avatar con iniziali + Nome */}
        {/* Mobile: Solo Nome (no avatar) */}
        <div className="flex items-center sm:flex-col sm:items-center gap-3 sm:gap-0">
          {/* Avatar - solo desktop */}
          <div className={`hidden sm:flex w-12 h-12 rounded-full items-center justify-center text-lg font-bold sm:mb-2 flex-shrink-0 ${
            isPresente
              ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
              : isAssente
                ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
          }`}>
            {getInitials(minore.nomeCompleto)}
          </div>

          {/* Nome */}
          <div className="min-w-0 flex-1 sm:flex-none sm:w-full">
            <p className="text-sm font-semibold text-gray-900 dark:text-white sm:text-center truncate">
              {minore.nomeCompleto || 'Senza nome'}
            </p>
            {/* Data di nascita solo se omonimo */}
            {isDuplicate && minore.dataNascita && (
              <p className="text-xs text-gray-400 dark:text-gray-500 sm:text-center">
                {formatBirthDate(minore.dataNascita)}
              </p>
            )}
          </div>
        </div>

        {/* Orario ingresso */}
        {isPresente && stato.orarioIngresso && (
          <p className="text-xs text-green-600 dark:text-green-400 sm:text-center mt-1">
            {stato.orarioIngresso}
          </p>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-3 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Zap className="h-6 w-6 mr-2 text-amber-500" />
              Quick Check-in
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        </div>
      </div>

      {/* Contatore */}
      <div className="flex items-center gap-4 text-sm">
        <span className="px-3 py-1.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
          {presenti} presenti
        </span>
        <span className="px-3 py-1.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 font-medium">
          {assenti} assenti
        </span>
        <span className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 font-medium">
          {daSegnare} da segnare
        </span>
      </div>

      {/* Griglia minori */}
      {isEducatore && altriMinori.length > 0 ? (
        <>
          {/* Sezione: I miei minori */}
          {mieiMinori.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
                I miei minori ({mieiMinori.length})
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {mieiMinori.map(renderMinoreCard)}
              </div>
            </div>
          )}

          {/* Sezione espandibile: Altri minori del centro */}
          <div>
            <button
              onClick={() => setShowAltri(!showAltri)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              {showAltri ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Altri minori del centro ({altriMinori.length})
            </button>
            {showAltri && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mt-3">
                {altriMinori.map(renderMinoreCard)}
              </div>
            )}
          </div>
        </>
      ) : (
        /* Coordinatori/Responsabili: tutti i minori in un'unica griglia */
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {minori.map(renderMinoreCard)}
        </div>
      )}

      {/* Istruzioni */}
      <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
        Tap = presente / Tap su presente = assente / Tasto destro = assente diretto
      </p>

      {/* Bottone conferma */}
      <div className="sticky bottom-20 md:bottom-4 flex justify-center">
        <button
          onClick={handleConfirm}
          disabled={saving || (presenti === 0 && assenti === 0)}
          className={`
            px-6 py-3 rounded-xl font-semibold text-white shadow-lg
            transition-all duration-200 flex items-center gap-2
            ${presenti > 0 || assenti > 0
              ? 'bg-primary-600 hover:bg-primary-700 active:scale-95'
              : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
            }
          `}
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Check className="h-5 w-5" />
          )}
          {saving ? 'Salvataggio...' : `Conferma Check-in (${presenti} presenti, ${assenti} assenti)`}
        </button>
      </div>
    </div>
  )
}
